/**
 * DIS05-S 2.5, 4.26, 4.30, 4.31: bilag to a billeddiagnostisk epikrise (XDIS05).
 * Communicate builds one XBIN01 per file (the builder 'Send korrespondance' uses) and
 * cites it in Reference/BIN. The same BIN01 limits as referral and korrespondance
 * attachments: at most 10 files, 100 MB together, the XML attachment types, and a zip
 * names its content (objectType). RefDescription is the file name (an..70, ISO-8859-1).
 */

import {
  checkReferralFile,
  needsZipObjectType,
  medbinObjectType,
  type ReferralFileLimitCode,
  type ZipObjectType,
} from '@/lib/referral/referralAttachments';
import { isLatin1 } from '@/lib/referral/xref01Validation';

export type EpikriseAttachment = {
  key: string;
  fileName: string;
  byteLength: number;
  file: File;
  zipObjectType?: ZipObjectType;
};

export type EpikriseAttachmentIssue = ReferralFileLimitCode | 'zip_type_missing' | 'name_not_latin1' | 'empty';

/** Problems per attachment, counted in order as the gateway counts them. */
export function epikriseAttachmentIssues(
  list: ReadonlyArray<Pick<EpikriseAttachment, 'key' | 'fileName' | 'byteLength' | 'zipObjectType'>>,
): Record<string, EpikriseAttachmentIssue> {
  const out: Record<string, EpikriseAttachmentIssue> = {};
  let count = 0;
  let total = 0;
  for (const a of list) {
    const limit = checkReferralFile({
      fileName: a.fileName,
      byteLength: a.byteLength,
      existingFileCount: count,
      existingTotalBytes: total,
    });
    if (!limit.ok) out[a.key] = limit.code;
    else if (a.byteLength < 1) out[a.key] = 'empty';
    else if (!isLatin1(a.fileName)) out[a.key] = 'name_not_latin1';
    else if (needsZipObjectType(a.fileName) && !a.zipObjectType) out[a.key] = 'zip_type_missing';
    count += 1;
    total += a.byteLength;
  }
  return out;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunk));
  }
  return btoa(binary);
}

export type EpikriseAttachmentBody = { fileName: string; fileBase64: string; objectType?: ZipObjectType };

export async function epikriseAttachmentsForRequest(list: readonly EpikriseAttachment[]): Promise<EpikriseAttachmentBody[]> {
  const out: EpikriseAttachmentBody[] = [];
  for (const a of list) {
    const bytes = new Uint8Array(await a.file.arrayBuffer());
    const objectType = medbinObjectType(a.fileName, a.zipObjectType);
    out.push({ fileName: a.fileName, fileBase64: bytesToBase64(bytes), ...(objectType ? { objectType } : {}) });
  }
  return out;
}
